import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  Cpu,
  Gauge,
  LayoutDashboard,
  Radio,
  RefreshCcw,
  Satellite,
  Server,
  ShieldCheck,
  SlidersHorizontal,
  TerminalSquare,
  Zap,
} from "lucide-react";
import MenuBar from "./components/MenuBar";
import SerialMonitor from "./components/SerialMonitor";
import VistaComunicaciones from "./components/VistaComunicaciones";
import VistaTelemetria from "./components/VistaTelemetria";
import VistaEPS from "./components/VistaEPS";
import VistaPayload from "./components/VistaPayload";
import SubsystemCommands from "./components/SubsystemCommands";
import Dashboard from "./components/Dashboard";
import "./App.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:3001";

const MODULES = [
  {
    id: 0,
    title: "Dashboard",
    description: "Tasas TX/RX y contenido enviado o recibido en los enlaces Esclavos–Maestro y Maestro–GUI.",
    icon: LayoutDashboard,
    badge: "Resumen",
  },
  {
    id: 1,
    title: "EPS",
    description: "Emulación de voltaje, potencia e impedancia según el firmware definitivo.",
    icon: Zap,
    badge: "Subsistema",
  },
  {
    id: 2,
    title: "Telemetría",
    description: "Emulación de variables ambientales, inerciales y de navegación del PIC.",
    icon: Gauge,
    badge: "Datos",
  },
  {
    id: 3,
    title: "Comunicaciones",
    description: "Emulación de RSSI, frecuencia y SNR según el firmware definitivo.",
    icon: Radio,
    badge: "COMMS",
  },
  {
    id: 4,
    title: "Payload",
    description: "Procesamiento Umbral o Sobel de la imagen 128×128 almacenada en el PIC.",
    icon: Satellite,
    badge: "Carga útil",
  },
  {
    id: 5,
    title: "Operación",
    description: "PING, ESTADO, LEER_VAR, DORMIR, REINICIAR y pruebas de diagnóstico de los esclavos.",
    icon: SlidersHorizontal,
    badge: "Control",
  },
  {
    id: 6,
    title: "Monitor Serial",
    description: "Web Serial para tarjeta local desde navegador y monitor serial del backend.",
    icon: TerminalSquare,
    badge: "Debug",
  },
];

function App() {
  const [selectedButton, setSelectedButton] = useState(null);
  const [viewKey, setViewKey] = useState(0);
  const [backendStatus, setBackendStatus] = useState({ online: false, serial: null });

  const activeModule = useMemo(
    () => MODULES.find((module) => module.id === selectedButton),
    [selectedButton]
  );

  const refreshBackendStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/health`);
      const data = await response.json();
      setBackendStatus({ online: Boolean(data?.ok), serial: data?.serial || null });
    } catch {
      setBackendStatus({ online: false, serial: null });
    }
  };

  useEffect(() => {
    refreshBackendStatus();
    const timer = setInterval(refreshBackendStatus, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleModuleClick = (index) => {
    setSelectedButton(index);
    setViewKey((current) => current + 1);
  };

  const renderView = () => {
    switch (selectedButton) {
      case 0:
        return <Dashboard />;
      case 1:
        return <VistaEPS />;
      case 2:
        return <VistaTelemetria />;
      case 3:
        return <VistaComunicaciones />;
      case 4:
        return <VistaPayload />;
      case 5:
        return <SubsystemCommands />;
      case 6:
        return <SerialMonitor />;
      default:
        return <WelcomePanel onSelect={handleModuleClick} />;
    }
  };

  return (
    <div className="obc-app">
      <div className="obc-background-glow obc-background-glow-a" />
      <div className="obc-background-glow obc-background-glow-b" />

      <header className="obc-header">
        <div className="obc-brand-block">
          <div className="obc-logo-mark">
            <Cpu size={30} />
          </div>

          <div>
            <p className="obc-kicker">On Board Computer</p>
            <h1>GUI de la OBC</h1>
            <p className="obc-subtitle">
              Panel de emulación, operación, protocolo UART y monitoreo de enlaces de la misión.
            </p>
          </div>
        </div>

        <div className={`obc-status-card ${backendStatus.online ? "is-online" : "is-offline"}`}>
          <span className="obc-status-dot" />
          <Server size={21} />
          <div>
            <strong>{backendStatus.online ? "Backend online" : "Backend offline"}</strong>
            <small>
              {backendStatus.serial?.connected
                ? `Serial ${backendStatus.serial.path} @ ${backendStatus.serial.baudRate}`
                : "Serial sin conexión"}
            </small>
          </div>
        </div>
      </header>

      <section className="obc-layout">
        <aside className="obc-sidebar">
          <div className="obc-sidebar-title">
            <LayoutDashboard size={18} />
            <span>Módulos</span>
          </div>

          <MenuBar selectedIndex={selectedButton} onButtonClick={handleModuleClick} />

          <div className="obc-help-card">
            <ShieldCheck size={20} />
            <div>
              <strong>Flujo final</strong>
              <p>
                La GUI genera JSON semántico; el backend convierte a tramas UART y el navegador puede enviarlas por Web Serial a la tarjeta local.
              </p>
            </div>
          </div>
        </aside>

        <main className="obc-main-panel">
          <div className="obc-view-header">
            <div>
              <p className="obc-kicker">Vista activa</p>
              <h2>{activeModule?.title || "Selecciona una función"}</h2>
              <span>{activeModule?.description || "Elige un módulo del menú lateral para iniciar la configuración."}</span>
            </div>

            <button
              className="obc-refresh-button"
              type="button"
              onClick={() => {
                refreshBackendStatus();
                setViewKey((current) => current + 1);
              }}
            >
              <RefreshCcw size={16} />
              Recargar
            </button>
          </div>

          <div className="obc-content-card" key={`${selectedButton ?? "home"}-${viewKey}`}>
            {renderView()}
          </div>
        </main>
      </section>
    </div>
  );
}

function WelcomePanel({ onSelect }) {
  return (
    <div className="obc-welcome">
      <div className="obc-welcome-hero">
        <div className="obc-welcome-icon">
          <Activity size={38} />
        </div>
        <h3>Selecciona un módulo para comenzar</h3>
        <p>
          Se conserva la esencia oscura con acento cyan, pero ahora el proyecto queda orientado al cierre:
          formularios en la GUI, backend generando tramas UART y envío Web Serial desde navegador cuando la tarjeta está conectada a la laptop.
        </p>
      </div>

      <div className="obc-module-grid">
        {MODULES.map((module) => {
          const Icon = module.icon;

          return (
            <button
              className="obc-module-card"
              key={module.id}
              type="button"
              onClick={() => onSelect(module.id)}
            >
              <div>
                <span>{module.badge}</span>
                <strong>{module.title}</strong>
                <p>{module.description}</p>
              </div>
              <Icon size={26} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default App;
